import React, { useEffect, useState } from "react";
import { auth, db } from "../backend/firebase";
import {
  collection,
  doc,
  addDoc,
  deleteDoc,
  serverTimestamp,
  getDocs,
} from "firebase/firestore";
import Dataset from "./Dataset";
import { Link, useNavigate } from "react-router-dom";
import { ClipLoader } from "react-spinners";
import { motion } from "motion/react";

const DatasetSource = ({ datasetDetails }) => {
  const [newSource, setNewSource] = useState("");
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sendLoad, setSendLoad] = useState(false);
  const [linkPreviews, setLinkPreviews] = useState({});
  const [failedPreviews, setFailedPreviews] = useState(new Set());
  const navigate = useNavigate();

  const isValidUrl = (string) => {
    try {
      const input = string.trim().startsWith("www.")
        ? `https://${string.trim()}`
        : string.trim();
      new URL(input);
      return true;
    } catch {
      return false;
    }
  };

  const isUUID = (string) => {
    const uuidRegex =
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    return uuidRegex.test(string.trim());
  };

  const fetchLinkPreview = async (url, sourceId) => {
    // Skip if we've already failed to fetch this preview
    if (failedPreviews.has(sourceId)) {
      return;
    }

    try {
      // Try multiple approaches to get link preview
      let previewData = null;

      // Approach 1: Direct fetch with timeout
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000);

        const response = await fetch(url, {
          signal: controller.signal,
          headers: {
            "User-Agent":
              "Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/91.0.4472.124 Safari/537.36",
          },
        });
        clearTimeout(timeoutId);

        if (response.ok) {
          const html = await response.text();
          previewData = extractMetaData(html, url);
        }
      } catch (directError) {
        console.log("Direct fetch failed, trying meta tag approach...");
      }

      // Approach 2: Use link-preview API if direct fetch fails
      if (!previewData) {
        try {
          const apiResponse = await fetch(
            `https://api.linkpreview.net/?key=public&q=${encodeURIComponent(
              url
            )}`
          );
          if (apiResponse.ok) {
            const data = await apiResponse.json();
            previewData = {
              title: data.title || "No title available",
              description: data.description || "No description available",
              image: data.image || null,
              url: data.url || url,
            };
          }
        } catch (apiError) {
          console.log("Link preview API failed");
        }
      }

      // Approach 3: Fallback - create basic preview from URL
      if (!previewData) {
        const domain = new URL(url).hostname;
        previewData = {
          title: domain,
          description: "Click to visit this website",
          image: null,
          url: url,
        };
      }

      setLinkPreviews((prev) => ({
        ...prev,
        [sourceId]: previewData,
      }));
    } catch (error) {
      console.error("Error fetching link preview:", error);
      // Mark this preview as failed to avoid retries
      setFailedPreviews((prev) => new Set([...prev, sourceId]));

      // Create fallback preview
      const domain = new URL(url).hostname;
      setLinkPreviews((prev) => ({
        ...prev,
        [sourceId]: {
          title: domain,
          description: "Website preview unavailable",
          image: null,
          url: url,
        },
      }));
    }
  };

  const extractMetaData = (html, url) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");

    const getMetaContent = (name, property) => {
      return (
        doc
          .querySelector(`meta[${property}="${name}"]`)
          ?.getAttribute("content") ||
        doc.querySelector(`meta[name="${name}"]`)?.getAttribute("content")
      );
    };

    const title =
      doc.querySelector("title")?.textContent?.trim() ||
      getMetaContent("og:title") ||
      getMetaContent("twitter:title") ||
      "No title available";

    const description =
      getMetaContent("og:description") ||
      getMetaContent("twitter:description") ||
      getMetaContent("description") ||
      "No description available";

    const image =
      getMetaContent("og:image") ||
      getMetaContent("twitter:image") ||
      doc.querySelector('link[rel="icon"]')?.getAttribute("href") ||
      doc.querySelector('link[rel="shortcut icon"]')?.getAttribute("href");

    // Resolve relative image URLs
    const fullImageUrl = image ? new URL(image, url).href : null;

    return {
      title: title.substring(0, 100), // Limit title length
      description: description.substring(0, 150), // Limit description length
      image: fullImageUrl,
      url: url,
    };
  };

  const downloadTextAsFile = (text, filename = "notes.txt") => {
    const blob = new Blob([text], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleAddSource = async () => {
    if (!newSource.trim()) return;

    let sourceData = {
      created_at: serverTimestamp(),
    };

    const input = newSource.trim();

    if (isValidUrl(input)) {
      sourceData.link = input.startsWith("www.") ? `https://${input}` : input;
      sourceData.source_type = "link";
    } else if (isUUID(input)) {
      sourceData.uuid = input;
      sourceData.source_type = "uuid";
    } else {
      sourceData.text = input;
      sourceData.source_type = "text";
    }

    try {
      setSendLoad(true);
      const sourceRef = collection(
        doc(db, "datasets", datasetDetails.uuid),
        "Sources"
      );
      await addDoc(sourceRef, sourceData);
      setNewSource("");
      fetchSources();
    } catch (error) {
      console.error("Error adding source:", error);
    } finally {
      setSendLoad(false);
    }
  };

  const fetchSources = async () => {
    try {
      setLoading(true);
      const snapshot = await getDocs(
        collection(doc(db, "datasets", datasetDetails.uuid), "Sources")
      );
      const sourcesData = snapshot.docs.map((docSnap) => {
        const data = docSnap.data();
        return {
          ...data,
          id: docSnap.id,
          created_at: data.created_at,
        };
      });

      const sortedSources = sourcesData.sort((a, b) => {
        const dateA = a.created_at?.toDate().getTime() || 0;
        const dateB = b.created_at?.toDate().getTime() || 0;
        return dateB - dateA;
      });

      setSources(sortedSources);

      // Fetch link previews for link sources
      sortedSources.forEach((source) => {
        if (
          source.source_type === "link" &&
          source.link &&
          !linkPreviews[source.id] &&
          !failedPreviews.has(source.id)
        ) {
          fetchLinkPreview(source.link, source.id);
        }
      });
    } catch (error) {
      console.error("Error fetching sources:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSource = async (id) => {
    try {
      await deleteDoc(doc(db, "datasets", datasetDetails.uuid, "Sources", id));

      // Clean up preview data
      setLinkPreviews((prev) => {
        const newPreviews = { ...prev };
        delete newPreviews[id];
        return newPreviews;
      });

      setFailedPreviews((prev) => {
        const newFailed = new Set(prev);
        newFailed.delete(id);
        return newFailed;
      });

      fetchSources();
    } catch (error) {
      console.error("Error deleting source:", error);
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return "";
    try {
      const date = timestamp.toDate();
      return date.toLocaleString();
    } catch {
      return "";
    }
  };

  const getDomainFromUrl = (url) => {
    try {
      return new URL(url).hostname.replace("www.", "");
    } catch {
      return url;
    }
  };

  useEffect(() => {
    fetchSources();
  }, []);

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 15,
        }}
      >
        <ClipLoader color="grey" size={20} />
      </div>
    );
  }

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        margin: 10,
        flex: 1,
        height: "100%",
        minHeight: 200,
      }}
    >
      {datasetDetails.author === auth.currentUser?.uid && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <textarea
            value={newSource}
            onChange={(e) => {
              setNewSource(e.target.value);

              // Auto-expand height
              const el = e.target;
              el.style.height = "auto"; // reset
              el.style.height = el.scrollHeight + "px"; // adjust
            }}
            placeholder="Paste a web link, enter a SETA ID, or add notes"
            style={{
              width: "100%",
              backgroundColor: "#1a1a1a",
              borderRadius: 6,
              padding: 12,
              borderWidth: 0,
              color: "#ccc",
              outline: "none",
              maxHeight: 300,
              paddingTop: 20,
              paddingBottom: 20,
              resize: "none", // prevent manual drag resize
              overflow: "hidden", // required for auto-expanding
              fontFamily: "Inter, Segoe UI, sans-serif",
              fontSize: "0.95rem",
              lineHeight: 1.4,
            }}
            onKeyPress={(e) => e.key === "Enter" && handleAddSource()}
          />

          {newSource && (
            <motion.div
              onClick={sendLoad ? null : handleAddSource}
              style={{
                marginTop: 8,
                color: "white",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: newSource ? "#667eea" : null,
                cursor: "pointer",
                border: newSource ? "none" : "1px solid #1a1a1a",
                padding: 10,
                width: "30%",
                alignSelf: "flex-end",
                borderRadius: 5,
                opacity: sendLoad ? 0.5 : 1,
              }}
              initial={{ height: 0 }}
              animate={{ height: "auto" }}
              exit={{ height: 0 }}
            >
              {sendLoad ? <ClipLoader color="white" size={12} /> : "Add Source"}
            </motion.div>
          )}
        </div>
      )}

      {sources.length === 0 ? (
        <div
          style={{
            border: "1px dashed #333",
            borderRadius: 8,
            padding: "40px 20px",
            textAlign: "center",
            backgroundColor: "rgba(255, 255, 255, 0.02)",
            marginTop: 10
          }}
        >
          <p style={{ color: "white", fontSize: 17 }}>No sources added</p>
        </div>
      ) : (
        <div style={{ marginTop: 20 }}>
          {sources.map((source) => (
            <div
              key={source.id}
              style={{
                marginBottom: 15,
                padding: 10,
                borderWidth: 1,
                borderColor: "#2a2a2a",
                borderStyle: "solid",
                display: "flex",
                flexDirection: "column",
                position: "relative",
                borderRadius: 5,
              }}
            >
              {datasetDetails.author === auth.currentUser?.uid && (
                <span
                  onClick={() => handleDeleteSource(source.id)}
                  style={{
                    position: "absolute",
                    top: 10,
                    right: 10,
                    color: "red",
                    cursor: "pointer",
                    fontSize: 12,
                  }}
                >
                  Delete
                </span>
              )}

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  marginBottom: 8,
                }}
              >
                <span
                  style={{
                    backgroundColor:
                      (source.source_type === "link" && "#e3f2fd") ||
                      (source.source_type === "uuid" && "#f3e5f5") ||
                      "#e8f5e8",
                    color:
                      (source.source_type === "link" && "#1565c0") ||
                      (source.source_type === "uuid" && "#7b1fa2") ||
                      "#2e7d32",
                    padding: "4px 12px",
                    fontSize: 12,
                    borderRadius: 12,
                    fontWeight: 500,
                  }}
                >
                  {source.source_type === "link"
                    ? "URL"
                    : source.source_type === "uuid"
                    ? "SETA"
                    : "TEXT"}
                </span>
                <span
                  style={{
                    fontSize: 12,
                    color: "#7a7a7a",
                    fontStyle: "italic",
                  }}
                >
                  {formatTimestamp(source.created_at)}
                </span>
              </div>

              {source.source_type === "link" && source.link && (
                <div>
                  {linkPreviews[source.id] ? (
                    <a
                      href={source.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        textDecoration: "none",
                        color: "inherit",
                        display: "block",
                      }}
                    >
                      <div
                        style={{
                          border: "1px solid #3a3a3a",
                          borderRadius: 8,
                          overflow: "hidden",
                          backgroundColor: "#1a1a1a",
                          transition: "all 0.2s ease",
                          cursor: "pointer",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.borderColor = "#667eea";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.borderColor = "#3a3a3a";
                        }}
                      >
                        {linkPreviews[source.id].image && (
                          <div
                            style={{
                              width: "100%",
                              height: "120px",
                              overflow: "hidden",
                            }}
                          >
                            <img
                              src={linkPreviews[source.id].image}
                              alt={linkPreviews[source.id].title}
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                              }}
                              onError={(e) => {
                                e.target.style.display = "none";
                              }}
                            />
                          </div>
                        )}
                        <div style={{ padding: "12px" }}>
                          <h4
                            style={{
                              margin: "0 0 8px 0",
                              color: "white",
                              fontSize: "14px",
                              fontWeight: "600",
                              lineHeight: "1.3",
                            }}
                          >
                            {linkPreviews[source.id].title}
                          </h4>
                          <p
                            style={{
                              margin: "0 0 8px 0",
                              color: "#a0a0a0",
                              fontSize: "12px",
                              lineHeight: "1.4",
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                            }}
                          >
                            {linkPreviews[source.id].description}
                          </p>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "6px",
                              color: "#4da6ff",
                              fontSize: "11px",
                            }}
                          >
                            <svg
                              width="12"
                              height="12"
                              viewBox="0 0 24 24"
                              fill="none"
                            >
                              <path
                                d="M10 6H6C4.89543 6 4 6.89543 4 8V18C4 19.1046 4.89543 20 6 20H16C17.1046 20 18 19.1046 18 18V14M14 4H20M20 4V10M20 4L10 14"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                            <span>{getDomainFromUrl(source.link)}</span>
                          </div>
                        </div>
                      </div>
                    </a>
                  ) : (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: "20px",
                        color: "#7a7a7a",
                        fontSize: "12px",
                      }}
                    >
                      <ClipLoader color="#7a7a7a" size={14} />
                      <span style={{ marginLeft: "8px" }}>
                        Loading preview...
                      </span>
                    </div>
                  )}
                </div>
              )}

              {source.source_type === "uuid" && source.uuid && (
                <Link
                  to={`/dataset/${source.uuid}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ textDecoration: "none" }}
                >
                  <div onClick={(e) => e.stopPropagation()}>
                    <Dataset datasetid={source.uuid} goNumb={true} />
                  </div>
                </Link>
              )}

              {source.source_type === "text" && source.text && (
                <div>
                  <div
                    style={{
                      color: "#e0e0e0",
                      padding: "12px",
                      backgroundColor: "#1a1a1a",
                      borderRadius: "4px",
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                      fontSize: "14px",
                      lineHeight: "1.5",
                      marginBottom: "8px",
                      border: "1px solid #2a2a2a",
                    }}
                  >
                    {source.text}
                  </div>
                  <button
                    onClick={() =>
                      downloadTextAsFile(source.text, `notes-${source.id}.txt`)
                    }
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      backgroundColor: "transparent",
                      border: "1px solid #4da6ff",
                      color: "#4da6ff",
                      padding: "6px 12px",
                      borderRadius: "4px",
                      fontSize: "12px",
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = "#4da6ff";
                      e.currentTarget.style.color = "white";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = "transparent";
                      e.currentTarget.style.color = "#4da6ff";
                    }}
                  >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M21 15V16.2C21 17.8802 21 18.7202 20.673 19.362C20.3854 19.9265 19.9265 20.3854 19.362 20.673C18.7202 21 17.8802 21 16.2 21H7.8C6.11984 21 5.27976 21 4.63803 20.673C4.07354 20.3854 3.6146 19.9265 3.32698 19.362C3 18.7202 3 17.8802 3 16.2V15M17 10L12 15M12 15L7 10M12 15V3"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    Download as TXT
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default DatasetSource;
